import { XMarkIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { getAnaerobicThresholdGraphs } from "../../../api/anaerobic-threshold";
import { useScreenBreakpoint } from "../../../hooks/useScreenBreakpoint";
import useTestId from "../../../hooks/useTestId";
import { TestGraph } from "../../../types";
import Graph from "../Graph";
import Error from "../../Error";
import Loader from "../../AppLoader";
import { useGraphs } from "../../../context/GraphsProvider";
import { twMerge } from "tailwind-merge";
import useSWRImmutable from "swr/immutable";
import _ from "lodash";
import { useEffect } from "react";

type Props = {
    graphIdToEnlarge: string;
    setGraphIdToEnlarge: (graphId: string | null) => void;
    onVatRelease?: (vat: number) => void;
    onRCPRelease?: (rcp: number) => void;
    withoutMaxPoint?: boolean;
    compareMode?: boolean;
    disabled?: boolean;
    isReverseVATColor?: boolean;
};

const SingleGraphView = ({
    graphIdToEnlarge,
    setGraphIdToEnlarge,
    onVatRelease,
    onRCPRelease,
    withoutMaxPoint = false,
    compareMode = false,
    disabled = false,
}: Props) => {
    const testId = useTestId();
    const { graphs, graphsType } = useGraphs();

    // get the graphId to compare.
    const {
        data: comparedGraphId,
        error,
        isLoading,
    } = useSWRImmutable<null | TestGraph["graph_id"]>(
        testId && compareMode && graphsType === "anaerobic-threshold"
            ? `/anaerobic-threshold/${testId}/graphs/compare/${graphIdToEnlarge}`
            : null,
        async () => {
            const graphs = await getAnaerobicThresholdGraphs({
                testId,
                graphId: graphIdToEnlarge,
                compare: true,
            });
            if (!graphs) return null;
            return graphs[1].graph_id;
        }
    );

    const graphToEnlarge = graphs?.find(
        (graph) => graph.graph_id === graphIdToEnlarge
    );
    const graphToCompare = graphs?.find(
        (graph) => graph.graph_id === comparedGraphId
    );

    const { isLaptopXL, isLaptop } = useScreenBreakpoint();

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setGraphIdToEnlarge(null);
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [setGraphIdToEnlarge]);

    if (isLoading)
        return (
            <div className="w-full h-[700px] bg-white grid place-content-center">
                <Loader />
            </div>
        );

    if (error) return <Error />;

    if (!graphToEnlarge) {
        return (
            <div className="text-red-error m-auto text-center">
                Graph Not Found
            </div>
        );
    }

    const size =
        compareMode || isLaptop || isLaptopXL ? "compare-primary" : "xxl";

    return (
        <div
            className={`relative bg-white w-full h-full flex flex-col gap-1.5 _justify-center ${
                compareMode ? "p-1 pb-5 overflow-hidden" : "p-4 xl:p-12"
            }`}
        >
            <div className="flex w-full justify-center">
                <h2
                    className={twMerge(
                        "text-black font-bold text-3xl",
                        size === "compare-primary"
                            ? "xl:text-3xl pt-1 pb-2"
                            : "xl:text-[42px]"
                    )}
                >
                    {graphToEnlarge.title}
                </h2>
                <button
                    onClick={() => setGraphIdToEnlarge(null)}
                    className={`absolute z-10 hover:bg-gray-100 top-0 right-0 size-10 rounded-full ${
                        compareMode ? "m-4" : "m-8"
                    }`}
                >
                    <XMarkIcon className="w-6 h-6 m-auto" />
                </button>
            </div>
            <div
                className={twMerge(
                    `grow w-full flex justify-center items-center`,
                    graphsType === "anaerobic-threshold"
                        ? "gap-3 xl:gap-6"
                        : "gap-5 xl:gap-10",
                    !compareMode ? "items-center" : "items-stretch"
                )}
            >
                <GraphNavButton
                    setGraphIdToEnlarge={setGraphIdToEnlarge}
                    graphIdToEnlarge={graphIdToEnlarge}
                    hide={compareMode}
                    left
                />
                <div
                    className={twMerge(
                        "w-full max-w-[1100px] flex flex-col gap-3 grow",
                        compareMode &&
                            "h-[70vh] grid grid-rows-[60%_40%] place-content-center",
                        !compareMode && "max-h-[70vh]"
                    )}
                >
                    <Graph
                        graphData={graphToEnlarge}
                        size={size}
                        onVatRelease={onVatRelease}
                        withoutMaxPoint={withoutMaxPoint}
                        onRCPRelease={onRCPRelease}
                        disabled={disabled}
                        isReverseVATColor={graphToEnlarge.graph_id === "4"}
                    />
                    {compareMode && graphToCompare && (
                        <Graph
                            graphData={graphToCompare}
                            size="compare-secondary"
                            onVatRelease={onVatRelease}
                            withoutMaxPoint={withoutMaxPoint}
                            onRCPRelease={onRCPRelease}
                            disabled={disabled}
                            isReverseVATColor={graphToCompare.graph_id === "4"}
                        />
                    )}
                </div>
                <GraphNavButton
                    setGraphIdToEnlarge={setGraphIdToEnlarge}
                    graphIdToEnlarge={graphIdToEnlarge}
                    hide={compareMode}
                />
            </div>
        </div>
    );
};

const GraphNavButton = ({
    setGraphIdToEnlarge,
    graphIdToEnlarge,
    left,
    hide,
}: {
    setGraphIdToEnlarge: (graphId: string) => void;
    graphIdToEnlarge: string;
    left?: boolean;
    hide?: boolean;
}) => {
    const graphsContextValue = useGraphs();
    const graphs = graphsContextValue.graphs!;

    return hide ? null : (
        <button
            className="rounded-full flex shrink-0 size-10 xl:size-16 items-center justify-center hover:bg-blue-light"
            onClick={() => {
                const currentIndex = graphs.findIndex(
                    (graph) => graph.graph_id === graphIdToEnlarge
                );
                const nextIndex =
                    currentIndex === graphs.length - 1 ? 0 : currentIndex + 1;
                const previousIndex =
                    currentIndex === 0 ? graphs.length - 1 : currentIndex - 1;

                setGraphIdToEnlarge(
                    graphs[left ? previousIndex : nextIndex].graph_id
                );
            }}
        >
            <ChevronRightIcon
                className={`w-6 h-6 stroke-[4] ${left ? "rotate-180" : ""}`}
            />
        </button>
    );
};

export default SingleGraphView;
